import type {
  RegulatoryEntityRecord,
  RegulatoryEntityVerificationStatus,
  RegulatoryIdentity,
  RegulatoryIdentityKind,
} from '../counterparties/counterparties.dto.js';
import type { CompanyReceiverType } from '../shipments/party.dto.js';

export const regulatoryDirectoryCandidateStatusValues = [
  'OPEN',
  'RESOLVED_TO_ENTITY',
  'REJECTED',
] as const;
export type RegulatoryDirectoryCandidateStatus =
  (typeof regulatoryDirectoryCandidateStatusValues)[number];

export const regulatoryDirectoryCandidateSourceTypeValues = [
  'COMPANY_RECEIVER',
  'INTERNAL_LOCATION',
] as const;
export type RegulatoryDirectoryCandidateSourceType =
  (typeof regulatoryDirectoryCandidateSourceTypeValues)[number];

export const regulatoryDirectoryAuditActionValues = [
  'ENTITY_CREATED',
  'ENTITY_UPDATED',
  'ENTITY_VERIFIED',
  'ENTITY_REACTIVATED',
  'ENTITY_DEACTIVATED',
  'ENTITY_MERGED',
  'ENTITY_RELINKED',
  'ENTITY_ALIAS_ADDED',
  'CANDIDATE_OBSERVED',
  'CANDIDATE_REOPENED',
  'CANDIDATE_PROMOTED',
  'CANDIDATE_RESOLVED',
  'CANDIDATE_REJECTED',
] as const;
export type RegulatoryDirectoryAuditAction =
  (typeof regulatoryDirectoryAuditActionValues)[number];

export interface RegulatoryEntityAliasRecord {
  id: string;
  value: string;
  normalizedValue: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegulatoryDirectoryAuditEntryRecord {
  id: string;
  regulatoryEntityId?: string | null;
  candidateId?: string | null;
  actorUserId: string;
  action: RegulatoryDirectoryAuditAction;
  reason?: string | null;
  metadata?: Record<string, unknown> | null;
  occurredAt: string;
  createdAt: string;
}

export interface RegulatoryDirectoryCandidateSourceRecord {
  id: string;
  candidateId: string;
  sourceType: RegulatoryDirectoryCandidateSourceType;
  sourceCompanyId: string;
  sourceRecordId: string;
  sourceDisplayName: string;
  receiverType?: CompanyReceiverType | null;
  referenceCode?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  createdAt: string;
  updatedAt: string;
  lastObservedAt: string;
}

export interface RegulatoryDirectoryEntitySummary extends RegulatoryEntityRecord {
  mergedIntoRegulatoryEntityId?: string | null;
  aliasCount: number;
  linkedCompanyReceiverCount: number;
  linkedLocationCount: number;
  linkedInternalLocationCount: number;
}

export interface RegulatoryDirectoryEntityDetail extends RegulatoryDirectoryEntitySummary {
  aliases: RegulatoryEntityAliasRecord[];
}

export interface RegulatoryDirectoryCandidateRecord {
  id: string;
  identity: RegulatoryIdentity;
  displayName: string;
  receiverType?: CompanyReceiverType | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  status: RegulatoryDirectoryCandidateStatus;
  resolvedRegulatoryEntityId?: string | null;
  rejectedReason?: string | null;
  sourceCount: number;
  lastObservedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegulatoryDirectoryCandidateDetail extends RegulatoryDirectoryCandidateRecord {
  sources: RegulatoryDirectoryCandidateSourceRecord[];
}

export interface ListRegulatoryDirectoryEntitiesRequest {
  query?: string;
  kind?: RegulatoryIdentityKind;
  verificationStatus?: RegulatoryEntityVerificationStatus;
  receiverType?: CompanyReceiverType | null;
  limit?: number;
}

export interface ListRegulatoryDirectoryEntitiesResponse {
  items: RegulatoryDirectoryEntitySummary[];
  total: number;
}

export interface GetRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface CreateRegulatoryDirectoryEntityRequest {
  identity: RegulatoryIdentity;
  displayName: string;
  receiverType?: CompanyReceiverType | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  aliasValues?: string[];
}

export interface CreateRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface UpdateRegulatoryDirectoryEntityRequest {
  entityId: string;
  displayName?: string;
  receiverType?: CompanyReceiverType | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  aliasValues?: string[];
}

export interface UpdateRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface VerifyRegulatoryDirectoryEntityRequest {
  entityId: string;
  reason?: string | null;
}

export interface VerifyRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface DeactivateRegulatoryDirectoryEntityRequest {
  entityId: string;
  reason?: string | null;
}

export interface DeactivateRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface ReactivateRegulatoryDirectoryEntityRequest {
  entityId: string;
  reason?: string | null;
}

export interface ReactivateRegulatoryDirectoryEntityResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface MergeRegulatoryDirectoryEntitiesRequest {
  sourceEntityId: string;
  targetEntityId: string;
  reason?: string | null;
}

export interface MergeRegulatoryDirectoryEntitiesResponse {
  entity: RegulatoryDirectoryEntityDetail;
}

export interface ListRegulatoryDirectoryCandidatesRequest {
  query?: string;
  kind?: RegulatoryIdentityKind;
  status?: RegulatoryDirectoryCandidateStatus;
  limit?: number;
}

export interface ListRegulatoryDirectoryCandidatesResponse {
  items: RegulatoryDirectoryCandidateRecord[];
  total: number;
}

export interface GetRegulatoryDirectoryCandidateResponse {
  candidate: RegulatoryDirectoryCandidateDetail;
}

export interface PromoteRegulatoryDirectoryCandidateRequest {
  candidateId: string;
  existingEntityId?: string;
  displayName?: string;
  receiverType?: CompanyReceiverType | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  aliasValues?: string[];
  reason?: string | null;
}

export interface PromoteRegulatoryDirectoryCandidateResponse {
  candidate: RegulatoryDirectoryCandidateDetail;
  entity: RegulatoryDirectoryEntityDetail;
}

export interface ResolveRegulatoryDirectoryCandidateRequest {
  candidateId: string;
  entityId: string;
  reason?: string | null;
}

export interface ResolveRegulatoryDirectoryCandidateResponse {
  candidate: RegulatoryDirectoryCandidateDetail;
  entity: RegulatoryDirectoryEntityDetail;
}

export interface RejectRegulatoryDirectoryCandidateRequest {
  candidateId: string;
  reason: string;
}

export interface RejectRegulatoryDirectoryCandidateResponse {
  candidate: RegulatoryDirectoryCandidateDetail;
}

export interface ListRegulatoryDirectoryAuditEntriesRequest {
  entityId?: string;
  candidateId?: string;
  limit?: number;
}

export interface ListRegulatoryDirectoryAuditEntriesResponse {
  items: RegulatoryDirectoryAuditEntryRecord[];
}
